import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Loader, Svg, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Routes } from '@/_shared/constants/routes';
import { getDepartmentTeamMembersCall } from '@/departments/state';
import { getEmployeesMeCall } from '@/profile/state';

export const TeamList = () => {
  const t = useTranslations('dashboard.learning.teamList');
  const tShared = useTranslations();
  const MAX_USERS_DISPLAYED = 5;
  const navigate = useNavigate();
  const { leadership, fullName, shortName, role, email } = getEmployeesMeCall.hook();

  const {
    execute: executeGetDepartmentTeamMembers,
    loading: loadingGetDepartmentTeamMembers,
    error: errorGetDepartmentTeamMembers,
    response: responseGetDepartmentTeamMembers,
  } = getDepartmentTeamMembersCall.hook();

  const goToEmployeeProfile = (employeeId: string) => {
    navigate(Routes.TeamEmployeeDetails.replace(':employeeId', employeeId));
  };

  const goToTeam = () => {
    navigate(Routes.Team);
  };

  useEffect(() => {
    executeGetDepartmentTeamMembers({
      departmentId: leadership?.team?.departmentId || '',
      teamId: leadership?.team?.id || '',
    });
  }, [executeGetDepartmentTeamMembers, leadership]);

  if (loadingGetDepartmentTeamMembers) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetDepartmentTeamMembers) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  return (
    <Card p='s'>
      <Box
        alignItems='center'
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        mt='xxxs'
        pb='xs'
      >
        <Typography variant='h3'>{leadership?.team?.name}</Typography>
        <Box alignItems='center' flexDirection='row' display='flex'>
          <Svg name='users' size='xs' />
          <Typography ml='xxs' variant='h3'>
            {responseGetDepartmentTeamMembers?.totalItems || 0}
          </Typography>
        </Box>
      </Box>
      <Box alignItems='center' display='flex' flexDirection='row' mb='s'>
        <Box
          backgroundColor='primary'
          borderRadius='xl'
          alignItems='center'
          justifyContent='center'
          width='xxl'
          height='xxl'
          display='flex'
          flexDirection='row'
        >
          <Typography
            display='inline-flex'
            justifyContent='center'
            alignItems='center'
            variant='h1'
          >
            {shortName || ''}
          </Typography>
        </Box>
        <Box display='flex' flexDirection='column'>
          <Typography ml='xs' variant='h3'>
            {fullName || ''}
          </Typography>
          <Typography ml='xs' fontSize='xs' variant='p3/light'>
            {role || ''}
          </Typography>
        </Box>
      </Box>
      {responseGetDepartmentTeamMembers &&
        (responseGetDepartmentTeamMembers?.totalItems ?? 0) > 1 && (
          <Box mb='m'>
            <Typography variant='p3/label'>{t('title')}</Typography>
            <Box>
              {(responseGetDepartmentTeamMembers?.data ?? [])
                .filter(employee => employee.email !== email)
                .slice(0, 5)
                .map(employee => (
                  <Card
                    onClick={() => goToEmployeeProfile(employee.id)}
                    p='s'
                    key={employee.id}
                    mt='xs'
                    backgroundColor='grey/dark/background'
                    style={{ cursor: 'pointer' }}
                  >
                    <Box alignItems='center' display='flex' flexDirection='row'>
                      <Box
                        backgroundColor='primary'
                        borderRadius='xl'
                        alignItems='center'
                        justifyContent='center'
                        width='xxl'
                        height='xxl'
                        display='flex'
                        flexDirection='row'
                      >
                        <Typography
                          display='inline-flex'
                          justifyContent='center'
                          alignItems='center'
                          variant='h4'
                        >
                          {`${employee.firstName?.charAt(0) ?? ''}${employee.lastName?.charAt(0) ?? ''}`}
                        </Typography>
                      </Box>
                      <Box display='flex' flexDirection='column'>
                        <Typography ml='xs' variant='h4'>
                          {`${employee.firstName} ${employee.lastName}`}
                        </Typography>
                        <Typography ml='xs' fontSize='xs' variant='p3/light'>
                          {employee.role || ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                ))}
            </Box>
          </Box>
        )}
      <Button onClick={goToTeam} variant='primary' display='block' style={{ margin: '0 auto' }}>
        {t('cta')}{' '}
        {responseGetDepartmentTeamMembers &&
          (responseGetDepartmentTeamMembers?.totalItems ?? 0) > MAX_USERS_DISPLAYED && (
            <Typography as='span' variant='p2/light'>
              (+{Number(responseGetDepartmentTeamMembers?.totalItems ?? 0) - MAX_USERS_DISPLAYED})
            </Typography>
          )}
      </Button>
    </Card>
  );
};
